import type { SupabaseClient } from '@supabase/supabase-js';

export const NO_ACTIVITY_ALERT_ENABLED_KEY = 'no_activity_alert_enabled';
export const NO_ACTIVITY_HOURS_KEY = 'no_activity_hours';
export const DEFAULT_NO_ACTIVITY_HOURS = 48;
export const MIN_NO_ACTIVITY_HOURS = 1;
export const MAX_NO_ACTIVITY_HOURS = 720;

export interface CrmNoActivitySettings {
  enabled: boolean;
  hours: number;
}

export function parseNoActivityHours(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_NO_ACTIVITY_HOURS;
  return Math.min(MAX_NO_ACTIVITY_HOURS, Math.max(MIN_NO_ACTIVITY_HOURS, Math.round(parsed)));
}

export function getDefaultNoActivitySettings(): CrmNoActivitySettings {
  return { enabled: false, hours: DEFAULT_NO_ACTIVITY_HOURS };
}

export async function loadCrmNoActivitySettings(
  supabase: SupabaseClient,
  businessId: string,
): Promise<CrmNoActivitySettings> {
  const defaults = getDefaultNoActivitySettings();

  const [{ data: enabled }, { data: hours }] = await Promise.all([
    supabase.rpc('get_app_setting', {
      p_business_id: businessId,
      p_app_slug: 'crm',
      p_setting_key: NO_ACTIVITY_ALERT_ENABLED_KEY,
      p_default_value: false,
    }),
    supabase.rpc('get_app_setting', {
      p_business_id: businessId,
      p_app_slug: 'crm',
      p_setting_key: NO_ACTIVITY_HOURS_KEY,
      p_default_value: DEFAULT_NO_ACTIVITY_HOURS,
    }),
  ]);

  return {
    enabled: Boolean(enabled),
    hours: parseNoActivityHours(hours),
  };
}

/** True when the lead has a future follow-up scheduled (excludes from no-activity alerts). */
export function hasScheduledFollowUp(
  nextFollowUpAt: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!nextFollowUpAt) return false;
  const dueMs = new Date(nextFollowUpAt).getTime();
  return !Number.isNaN(dueMs) && dueMs > now.getTime();
}

export function resolveLastActivityAt(
  lastActivityAt: string | null | undefined,
  createdAt: string,
): string {
  if (lastActivityAt) {
    const ms = new Date(lastActivityAt).getTime();
    if (!Number.isNaN(ms)) return lastActivityAt;
  }
  return createdAt;
}

export function isLeadStaleNoActivity(
  lastActivityAt: string | null | undefined,
  createdAt: string,
  thresholdHours: number,
  now: Date = new Date(),
): boolean {
  const anchor = resolveLastActivityAt(lastActivityAt, createdAt);
  const anchorMs = new Date(anchor).getTime();
  if (Number.isNaN(anchorMs)) return false;

  const hoursSince = (now.getTime() - anchorMs) / (60 * 60 * 1000);
  return hoursSince >= thresholdHours;
}
